import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    OutlinedInput,
    Paper,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import type {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {useMemo, useRef, useState, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {RichTextField} from "../../common/newRichText";
import type {VariableCatalogue} from "../../common/newVariablePicker";
import {SurveyQuestionTypeElement} from "../../common/uiElements/SurveyQuestionTypeElement";
import SurveyQuestionMarkers from "../../surveys/shared/SurveyQuestionMarkers";
import {SURVEY_VARIABLES, surveyPickerVariables} from "../../surveys/shared/surveyVariables";

const NOTES = "Called the guest on Tuesday. The *room was cold* on both nights, the heating is fixed now.\n\n"
    + "&nbsp;\n\nNext steps:\n\n- Offer a voucher for the spa\n- Tell housekeeping";
const TITLE = "How was your stay, {{guestFirstName|there}}?";
const DESCRIPTION = "Your answers go straight to our team at **{{hotelName|us}}**. "
    + "It takes about two minutes, and you can read how we use them on [our website](https://example.com).";

/* The survey editor's first question of "Vor der Spa-Behandlung – Gesundheitsfragen", as on 2026-10-07. */
const CARD_QUESTION: SurveyQuestion = {
    type: SurveyQuestionType.MULTIPLE_CHOICE,
    isRequired: true,
    isSensitive: true,
};
const CARD_TITLE = "Trifft einer der folgenden Punkte auf Sie zu?";
const CARD_OPTIONS = [
    "Schwangerschaft",
    "Blutdruck oder Herzerkrankung",
    "Kürzliche Operation oder Verletzung",
    "Hauterkrankung",
    "Diabetes",
    "Nichts davon",
];

const SectionHeader = ({label}: {label: string}) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, mb: 1}}
    >
        {label}
    </Typography>
);

/** One case: the field, then what it would store, and a reload that reads the stored text back in. */
const Case = ({title, subtitle, value, onReload, children}: {
    title: string;
    subtitle: string;
    value: string;
    onReload: () => void;
    children: ReactNode;
}) => (
    <Stack gap={1}>
        <Box>
            <Typography variant="body1" sx={{fontWeight: 600}}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
        <Box sx={{maxWidth: 640}}>{children}</Box>
        <Stack direction="row" alignItems="flex-start" gap={1} sx={{maxWidth: 640}}>
            <Box
                component="pre"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                    fontFamily: "\"Roboto Mono\", SFMono-Regular, Consolas, monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    color: value ? "text.primary" : "text.disabled",
                }}
            >
                {value || "(empty)"}
            </Box>
            <Button size="small" color="inherit" startIcon={<ReplayIcon/>} onClick={onReload} sx={{flexShrink: 0}}>
                Reload
            </Button>
        </Stack>
    </Stack>
);

/** A value, plus a key that remounts its field from the stored text. */
const useStored = (initial: string) => {
    const [value, setValue] = useState(initial);
    const [key, setKey] = useState(0);
    return {value, setValue, key, reload: () => setKey(current => current + 1)};
};

/**
 * The canvas card of a multiple choice question, laid out as `SurveyEditorCanvas` and `ChoiceQuestionEditor` lay
 * it out: title, description and help text are rich, the options are plain outlined inputs.
 */
const SurveyQuestionCard = ({catalogue}: {catalogue: VariableCatalogue}) => {
    const {t} = useTranslation();
    const [title, setTitle] = useState(CARD_TITLE);
    const [description, setDescription] = useState("");
    const [help, setHelp] = useState("");
    const [options, setOptions] = useState(() => CARD_OPTIONS.map((label, index) => ({id: index, label})));
    const [addedId, setAddedId] = useState<number | null>(null);
    const nextIdRef = useRef(CARD_OPTIONS.length);

    const addOption = () => {
        const id = nextIdRef.current++;
        setAddedId(id);
        setOptions(current => [...current, {id, label: ""}]);
    };

    return (
        <Box sx={{maxWidth: 760, mx: "auto"}}>
            <Paper variant="outlined" sx={{borderRadius: 2, p: {xs: 2.5, sm: 4}}}>
                <Stack gap={3}>
                    <Stack gap={1}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <SurveyQuestionMarkers question={CARD_QUESTION} size="standalone" gap={0}/>
                            <SurveyQuestionTypeElement type={CARD_QUESTION.type}/>
                        </Stack>

                        <RichTextField
                            value={title}
                            onChange={setTitle}
                            placeholder={t("Question title")}
                            singleLine
                            textSize="title"
                            alignment
                            emoji
                            variables={catalogue}
                        />
                        <RichTextField
                            value={description}
                            onChange={setDescription}
                            placeholder={t("Add context or instructions for guests")}
                            alignment
                            emoji
                            variables={catalogue}
                        />
                        <Stack gap={0.5} sx={{mt: 1}}>
                            <Stack direction="row" alignItems="center" gap={0.5} sx={{color: "text.secondary"}}>
                                <HelpOutlineOutlinedIcon sx={{fontSize: 16}}/>
                                <Typography variant="body2">{t("Help text")}</Typography>
                            </Stack>
                            <RichTextField
                                value={help}
                                onChange={setHelp}
                                placeholder={t("Add help guests can open with the ? button")}
                                alignment
                                emoji
                                variables={catalogue}
                            />
                        </Stack>
                    </Stack>

                    <Stack gap={0.5}>
                        <Typography variant="body2" color="text.secondary">{t("Options")}</Typography>
                        <Stack gap={1}>
                            {options.map((option, index) => (
                                <Stack key={option.id} direction="row" alignItems="center" gap={1}>
                                    <OutlinedInput
                                        size="small"
                                        fullWidth
                                        value={option.label}
                                        autoFocus={option.id === addedId}
                                        inputProps={{"aria-label": `${t("Option")} ${index + 1}`}}
                                        onChange={event => setOptions(current => current.map(entry => (
                                            entry.id === option.id ? {...entry, label: event.target.value} : entry
                                        )))}
                                    />
                                    <IconButton
                                        size="small"
                                        aria-label={t("Delete")}
                                        onClick={() => setOptions(current => current.filter(entry => (
                                            entry.id !== option.id
                                        )))}
                                    >
                                        <DeleteOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            ))}
                            <Button size="small" startIcon={<AddIcon/>} sx={{alignSelf: "flex-start"}} onClick={addOption}>
                                {t("Add option")}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
};

const RichTextTestPage = () => {
    const {t} = useTranslation();

    const catalogue = useMemo<VariableCatalogue>(
        () => ({variables: surveyPickerVariables(SURVEY_VARIABLES, t, "en"), preview: {language: "en"}}),
        [t],
    );
    // The card's survey is German, so fallbacks and samples are too.
    const cardCatalogue = useMemo<VariableCatalogue>(
        () => ({variables: surveyPickerVariables(SURVEY_VARIABLES, t, "de"), preview: {language: "de"}}),
        [t],
    );

    const [singleLine, setSingleLine] = useState(false);
    const [alignment, setAlignment] = useState(false);
    const [emoji, setEmoji] = useState(false);
    const [variables, setVariables] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const playground = useStored("");

    const notes = useStored(NOTES);
    const title = useStored(TITLE);
    const description = useStored(DESCRIPTION);
    const readOnly = useStored(NOTES);
    const dialogNotes = useStored("");
    const [dialogOpen, setDialogOpen] = useState(false);

    const toggle = (label: string, checked: boolean, onChange: (checked: boolean) => void) => (
        <FormControlLabel
            label={label}
            control={<Switch size="small" checked={checked} onChange={event => onChange(event.target.checked)}/>}
        />
    );

    // PageShell bounds the tab at 100dvh and clips it, so this page owns its own scroller.
    return (
        <Box sx={{flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto"}}>
            <Box sx={{p: {xs: 2, sm: 3}, maxWidth: 920, mx: "auto"}}>
                <Typography variant="h5" sx={{fontWeight: 700}}>
                    Rich text
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    RichTextField from common/newRichText — markdown in, markdown out. Bold, italic, underline,
                    links, undo and redo always; lists on multi-line fields; alignment (one dropdown), emoji and
                    variables only where a field switches them on. Under each field: what it would store. Reload
                    builds the field again from that text, so you can check that nothing is lost on the way back in.
                </Typography>

                {/* ---------------- Survey question card ---------------- */}
                <Box sx={{mb: 4}}>
                    <SectionHeader label="Survey question card"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        The densest place the field sits: a survey canvas card with three rich fields (the title at
                        the canvas's size) and six plain outlined options.
                    </Typography>
                    <SurveyQuestionCard catalogue={cardCatalogue}/>
                </Box>

                {/* ---------------- Switches ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="Switches"/>
                    <Stack direction="row" flexWrap="wrap" columnGap={2} sx={{mb: 2}}>
                        {toggle("singleLine", singleLine, setSingleLine)}
                        {toggle("alignment", alignment, setAlignment)}
                        {toggle("emoji", emoji, setEmoji)}
                        {toggle("variables", variables, setVariables)}
                        {toggle("disabled", disabled, setDisabled)}
                    </Stack>
                    <Case
                        title="One field, every switch"
                        subtitle="Changing singleLine, alignment, emoji or variables builds the field again from the stored text."
                        value={playground.value}
                        onReload={playground.reload}
                    >
                        <RichTextField
                            key={playground.key}
                            value={playground.value}
                            onChange={playground.setValue}
                            placeholder="Type, or try / for a variable and : for an emoji"
                            singleLine={singleLine}
                            alignment={alignment}
                            emoji={emoji}
                            variables={variables ? catalogue : undefined}
                            disabled={disabled}
                        />
                    </Case>
                </Paper>

                {/* ---------------- Where it goes ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label="Where it goes"/>
                    <Stack divider={<Divider flexItem/>} spacing={3}>
                        <Case
                            title="Review notes"
                            subtitle="No switches · four lines tall · a blank line and a list in the sample"
                            value={notes.value}
                            onReload={notes.reload}
                        >
                            <RichTextField
                                key={notes.key}
                                value={notes.value}
                                onChange={notes.setValue}
                                placeholder="Notes"
                                minRows={4}
                            />
                        </Case>

                        <Case
                            title="Survey question title"
                            subtitle="singleLine · alignment · emoji · variables (the survey's 28)"
                            value={title.value}
                            onReload={title.reload}
                        >
                            <RichTextField
                                key={title.key}
                                value={title.value}
                                onChange={title.setValue}
                                placeholder="Question title"
                                singleLine
                                alignment
                                emoji
                                variables={catalogue}
                            />
                        </Case>

                        <Case
                            title="Survey description"
                            subtitle="alignment · emoji · variables"
                            value={description.value}
                            onReload={description.reload}
                        >
                            <RichTextField
                                key={description.key}
                                value={description.value}
                                onChange={description.setValue}
                                placeholder="Add context or instructions for guests"
                                alignment
                                emoji
                                variables={catalogue}
                            />
                        </Case>

                        <Case
                            title="Disabled"
                            subtitle="A read-only user's notes: no toolbar, nothing to edit"
                            value={readOnly.value}
                            onReload={readOnly.reload}
                        >
                            <RichTextField
                                key={readOnly.key}
                                value={readOnly.value}
                                onChange={readOnly.setValue}
                                placeholder="Notes"
                                minRows={4}
                                disabled
                            />
                        </Case>
                    </Stack>
                </Paper>

                {/* ---------------- Dialog ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, borderRadius: 2}}>
                    <SectionHeader label="Inside a dialog"/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        The submission dialog's notes sit in a dialog: the link, emoji and variable popups have to
                        open above it, and Escape in a popup closes only the popup.
                    </Typography>
                    <Button variant="outlined" onClick={() => setDialogOpen(true)}>Open dialog</Button>
                    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Notes in a dialog</DialogTitle>
                        <DialogContent>
                            <Box sx={{pt: 1}}>
                                <Case
                                    title="Notes"
                                    subtitle="emoji · variables, to reach every popup"
                                    value={dialogNotes.value}
                                    onReload={dialogNotes.reload}
                                >
                                    <RichTextField
                                        key={dialogNotes.key}
                                        value={dialogNotes.value}
                                        onChange={dialogNotes.setValue}
                                        placeholder="Notes"
                                        minRows={4}
                                        emoji
                                        variables={catalogue}
                                    />
                                </Case>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialogOpen(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Box>
        </Box>
    );
};

export default RichTextTestPage;
